import React, { useState, useRef, useContext, useEffect } from 'react'
import styles from './index.module.css'
import { cn } from '@s4l/utility-css'
import { FormContext } from '../context'
import type { Rule } from '../Validations'
import Typography from '../../Typography'

interface IInputProps {
  name: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel'
  defaultValue?: string
  onChange?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  validations?: Array<ReturnType<Rule>>
}

const Input = ({
  type = 'text',
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  name,
  validations,
  placeholder,
}: IInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [value, setValue] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)

  const { form, setFormValue, setFormValidation, errors, setInputInitialized } =
    useContext(FormContext)

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur?.()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    onChange?.(newValue)
    setFormValue?.(e.target.name, newValue, true)
  }

  useEffect(() => {
    if (defaultValue !== undefined) {
      setFormValue(name, defaultValue, false)
      setInputInitialized(name, true)
    }
  }, [defaultValue, name, setFormValue, setInputInitialized])

  useEffect(() => {
    const valueCandidate = form[name]
    if (typeof valueCandidate === 'string' && defaultValue === undefined) {
      setValue(valueCandidate)
    }

    if (defaultValue === undefined && form[name] === undefined) {
      setFormValue(name, '', true)
    }
  }, [form, name, defaultValue, setFormValue])

  useEffect(() => {
    if (!validations) return

    setFormValidation(name, validations)
    setInputInitialized(name, false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const hasError = !!errors[name]

  return (
    <>
      <input
        name={name}
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        className={cn(styles.input, {
          [styles.focused]: isFocused,
          [styles.hasError]: hasError,
        })}
        data-state-error={hasError}
        value={value ?? ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
      />
      {hasError && (
        <Typography variant="caption" size="l" color="var(--color-error-500)">
          {errors[name]}
        </Typography>
      )}
    </>
  )
}

Input.displayName = 'Form.Input'
export default Input
